<script setup lang="ts">
import { MAIN_PAGE_TITLE, MAIN_PAGE_DESCRIPTION } from '@/shared/constants/seoConstants';

import { ROLE_TYPE_LAWYER } from '@/shared/constants/roleTypeConstants';
import { ROOT_URL_HOST } from '@/shared/constants/contactConstants';
import { MAIN_PAGE_FAQ_QUESTIONS } from '@/shared/constants/faqConstants';
import { useModalStore } from '@/shared/ui/AppModal/store/modal';
import { useUserStore } from '@/entities/user/store';
import SchemaOrg from '@/shared/ui/SchemaOrg/index';

import MainSection from '@/pages/main/ui/MainSection.vue';
import HowFindLawyer from '@/pages/main/ui/HowFindLawyer.vue';
import TopSpecialistsSection from '@/pages/main/ui/TopSpecialistsSection.vue';
import FindLawyerSection from '@/pages/main/ui/FindLawyerSection.vue';
import PublicationsSection from '@/pages/main/ui/PublicationsSection.vue';
import FaqSection from '@/widgets/FaqSection/index';
import { mainPageGraph, faqData } from '@/pages/main/lib/schemaConstants';

const modalStore = useModalStore();
const userStore = useUserStore();
const isMobile: boolean = useIsMobile();
const route = useRoute();
const router = useRouter();

const url = computed(() => `${ROOT_URL_HOST}${route.path}`);
const { isAuth } = storeToRefs(userStore);
const redirectQuery = computed(() => route?.query?.redirect_to);
const roleTypeQuery = computed(() => Number(route?.query?.role_type) || ROLE_TYPE_LAWYER);

const showAuthModal = () => {
  if (!isAuth.value) {
    modalStore.setModalState('auth', true, {
      isLogin: true,
      mode: roleTypeQuery.value,
    });
  }
};

onMounted(() => {
  if (redirectQuery.value && !isAuth.value) {
    nextTick(() => {
      showAuthModal();
    });
  }
  if (redirectQuery.value && isAuth.value) {
    const modifiedRedirect = String(redirectQuery.value).endsWith('/')
      ? redirectQuery.value
      : `${redirectQuery.value}/`;
    router.replace(String(modifiedRedirect));
  }
});

useSeoMeta({
  title: MAIN_PAGE_TITLE,
  description: MAIN_PAGE_DESCRIPTION,
  ogTitle: MAIN_PAGE_TITLE,
  ogDescription: MAIN_PAGE_DESCRIPTION,
  ogSiteName: MAIN_PAGE_TITLE,
  ogType: 'website',
  ogUrl: url,
  ogLocale: 'ru',
  ogImage: `${ROOT_URL_HOST}/og-image.png`,
});

useHead({
  meta: [
    {
      property: 'vk:image',
      content: `${ROOT_URL_HOST}/og-image.png`,
    },
  ],
  link: [
    {
      rel: 'preload',
      as: 'image',
      href: isMobile
        ? '/images/main-page/main-bg-mobile.webp'
        : '/images/main-page/main-bg.webp',
      fetchpriority: 'high',
    },
  ],
});
</script>

<template>
  <div class="main-page">
    <SchemaOrg :graph="mainPageGraph" />
    <SchemaOrg type="FAQPage" :data="faqData" />
    <MainSection />
    <HowFindLawyer />
    <TopSpecialistsSection />
    <FindLawyerSection />
    <PublicationsSection />
    <FaqSection :questions="MAIN_PAGE_FAQ_QUESTIONS" />
  </div>
</template>
